import { useContext, useEffect, useMemo, useRef } from 'react';
import { Outlet, useNavigation } from 'react-router-dom';
import { Spinner } from '@ds';
import { MenuItemType, SideMenu } from '@ui';
import { AuthContext } from '@lp/core/auth';
import { LP_MAIN_MENU } from '@lp/core/routing/const';
import { MdOutlineLogin } from 'react-icons/md';
import { CloseToastIcon } from '@icons';
import { ChatProviderWrapper } from '@features/contexts/ChatProvider';
import { useWsFallbackConfig } from '@lp/core/routing/useWsFallbackConfig';
import { identitySyncConfig } from '@lp/core/routing/identity-sync.config';
import { AdminPermissions, CaseBoardPermissions, ChatViewPermissions } from '@/permissions';
import { useNotifications } from '@/hooks/useNotifications/useNotifications';
import { useIdentitySyncFailedNotifications } from '@/hooks/useIdentitySyncFailedNotifications/useIdentitySyncFailedNotifications';
import { useInitializeSettings } from '@/hooks/useInitializeSettings';
import { getFullName } from '@/utils/helpers';
import { queryClient } from '@/queries';
import { hubQueries } from '@/queries/hub';
import { CountriesDataProvider } from '@/contexts/CountriesDataProvider';
import { WsFallbackRefetch } from '@/ws/fallback';

const getUserMenu =
  (checkPermission: (permission: AdminPermissions) => boolean) =>
  (action: (type: 'link' | 'action', to: string) => void): MenuItemType[] => {
    const hasAdminPermission = checkPermission(AdminPermissions.AdminView);
    const menuItems: Array<MenuItemType | undefined> = [
      {
        key: 'profile',
        type: 'link',
        to: 'profile',
        label: <span className="text-m-r text-base-5">My Profile</span>,
        onAction: action,
      },
      {
        key: 'settings',
        type: 'link',
        to: 'profile/settings',
        label: <span className="text-m-r text-base-5">Settings</span>,
        onAction: action,
      },
      hasAdminPermission
        ? {
            key: 'admin',
            type: 'link',
            to: 'admin',
            label: <span className="text-m-r text-base-5">Admin Panel</span>,
            onAction: action,
          }
        : undefined,
      {
        key: 'sign-out',
        type: 'action',
        to: 'sign-out',
        label: (
          <div className="flex items-center gap-2 text-danger">
            <MdOutlineLogin size={16} />
            <span className="text-m-r">Log Out</span>
          </div>
        ),
        onAction: action,
      },
    ];

    return menuItems.filter((v): v is MenuItemType => Boolean(v));
  };

export const RootLayout = () => {
  const { isUserDataInitialized, logout, checkPermission, profile, employeeData } = useContext(AuthContext);
  const settingsData = useInitializeSettings(profile?.userId, isUserDataInitialized);
  const { state } = useNavigation();
  const userName = getFullName(employeeData?.employee?.firstName, employeeData?.employee?.lastName);
  const photoPath = employeeData?.employee?.profilePhoto;
  const clearAllButtonRef = useRef<HTMLButtonElement | null>(null);

  useEffect(() => {
    if (isUserDataInitialized) {
      queryClient.prefetchQuery(hubQueries.all());
    }
  }, [isUserDataInitialized]);

  const { notificationCount, closeAllToast } = useNotifications(settingsData, true);
  useIdentitySyncFailedNotifications(identitySyncConfig);

  useEffect(() => {
    const onPointerDownCapture = (event: PointerEvent) => {
      const target = event.target as Node | null;
      const button = clearAllButtonRef.current;

      if (!button || !target) return;
      if (!button.contains(target)) return;

      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation?.();

      closeAllToast();
    };

    document.addEventListener('pointerdown', onPointerDownCapture, true);

    return () => {
      document.removeEventListener('pointerdown', onPointerDownCapture, true);
    };
  }, [closeAllToast]);

  const mainMenu = useMemo(() => {
    return LP_MAIN_MENU.filter(
      (menuItem) =>
        !menuItem.permissions.length || menuItem.permissions.some((permission) => checkPermission(permission)),
    ).map((menuItem) => {
      if (menuItem.children) {
        return {
          ...menuItem,
          children: menuItem.children.filter(
            (child) => !child.permissions.length || child.permissions.some((permission) => checkPermission(permission)),
          ),
        };
      }

      return menuItem;
    });
  }, [checkPermission]);

  const hasChatsPermission = checkPermission(ChatViewPermissions.ViewChat);
  const hasCaseBoardPermission = checkPermission(CaseBoardPermissions.CaseBoardRead);
  const wsFallbackConfig = useWsFallbackConfig(hasCaseBoardPermission);

  return (
    <div className="w-full h-dvh flex overflow-hidden">
      <WsFallbackRefetch config={wsFallbackConfig} />
      {notificationCount > 1 && (
        <div className="absolute right-[4%] top-2 z-[10000]">
          <button
            ref={clearAllButtonRef}
            type="button"
            className="
              flex items-center gap-2
              rounded-full
              border border-base-40
              bg-base-50
              px-4 py-2
              shadow-sm
              hover:bg-base-60
              active:scale-[0.98]
              transition-all
            "
          >
            <span className="text-m-sb text-black">Clear all</span>
            <CloseToastIcon />
          </button>
        </div>
      )}

      {state === 'loading' && (
        <div
          className="absolute top-0 left-0 w-full h-full z-999 flex items-center justify-center"
          style={{ backgroundColor: 'rgba(51, 53, 63, 0.4)' }}
        >
          <div className="flex items-center justify-center p-2 rounded-md">
            <Spinner size="md" />
          </div>
        </div>
      )}

      {isUserDataInitialized && profile ? (
        <ChatProviderWrapper userId={profile.userId} hasPermissions={hasChatsPermission}>
          <SideMenu
            logout={logout}
            getUserMenu={getUserMenu(checkPermission)}
            mainMenu={mainMenu}
            userName={userName}
            photoPath={photoPath}
          />
          <div className="grow overflow-auto bg-base-70" data-main-content>
            <CountriesDataProvider>
              <Outlet />
            </CountriesDataProvider>
          </div>
        </ChatProviderWrapper>
      ) : (
        <div className="w-full h-full flex justify-center items-center">
          <Spinner />
        </div>
      )}
    </div>
  );
};
